import { useNavigate } from 'react-router';
import { Box } from '@mui/material';

import { usePlanDetail } from '../hooks/use-plan-detail';
import { PlanHeader } from '../components/common/plan-header';
import { PlanActionsButton } from '../components/common/actions-button';
import { usePlanDetailUnsavedChanges } from '../hooks/use-plan-unsaved-changes';
import { UpdateSection, CreateSection, DeleteSection } from '../components/confirm';
import { PropertySection } from '../components/confirm/property-section';
import {
  createOriginalPlansMap,
  convertUpdatePlans,
  convertCreatePlans,
  convertDeletePlans,
  isUntouchedBlankCreateRow,
} from '../hooks/use-confirm';
import { updatePlanById } from '../services/plans.service';
import { getPropertySectionDisplayName } from '../utils/property-display';

import { PlanEditView } from './plan-confirm-page';
import { PlanEditViewMobile } from './mobiles/plan-edit-view-mobile';

import type { UpdateFullPlanRequest, UpdatePlanRequest } from '../types/plan.types';

import { usePageExitReset } from '@/features/companies/hooks/use-page-exit-reset';
import { snackbarType } from '@/lib/enums';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useSnackbar } from '@/hooks/use-snackbar';
import { useSubmitting } from '@/hooks/use-submitting';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { MESSAGES } from '@/lib/constants';
import { paths } from '@/config/paths';

// Import CSS for confirm view tables
import '../styles/handsontable-confirm-common.style.css';
import '../styles/handsontable-confirm-update.styles.css';
import '../styles/handsontable-confirm-create.styles.css';
import '../styles/handsontable-confirm-delete.styles.css';

export const PlanDetailPage = () => {
  const navigate = useNavigate();
  const {
    planDetail,
    filteredPlanDeliveries,
    tableData,
    fullTableData,
    visibleRowIndices,
    editedRowIndices,
    oldRecordIndices,
    filteredOriginalTableData,
    tableUpdateData,
    tableCreateNewData,
    tableDeletedData,
    plantOptions,
    witnessCompanyOptions,
    filters,
    setFilters,
    countPlans,
    rowsWithErrors,
    setRowsWithErrors,
    updateSectionErrors,
    setUpdateSectionErrors,
    createSectionErrors,
    setCreateSectionErrors,
    viewMode,
    handleSubmit,
    handleBackToEdit,
    handleAddRow,
    handleAddRowAbove,
    handleAddRowBelow,
    handleDeleteRows,
    handleTableDataChange,
    handleUpdateSectionDataChange,
    handleCreateSectionDataChange,
    resetAllForms,
  } = usePlanDetail();

  const { setIsLoading } = useLoadingProgress();
  const { setConfirmDialog } = useConfirmDialog();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsSubmitting } = useSubmitting();
  const isMobile = useIsMobile();

  // Check for unsaved changes
  // IMPORTANT: Only check changes for records that are currently visible (filtered)
  // If user hides old records, changes to those records should not enable submit button
  // This applies to both submit button AND page exit warning
  // If user hides old records and changes them, they can leave page without warning
  const hasUnsavedChanges = usePlanDetailUnsavedChanges(
    tableData, // filteredTableData
    filteredOriginalTableData // filteredOriginalTableData
  );
  // Reset all forms when leaving page or reloading (with warning if unsaved changes)
  // IMPORTANT: Only warn about changes in visible records (filtered)
  // If user hides old records and changes them, no warning when leaving page
  usePageExitReset(resetAllForms, true, hasUnsavedChanges);

  const handleCancel = () => {
    if (viewMode === 'confirm') {
      handleBackToEdit();
    } else {
      // When canceling from edit view, if user say yes -> redirect to plans list
      navigate('/plans');
    }
  };

  // Check if there are any validation errors in confirm view
  const hasConfirmErrors = updateSectionErrors.length > 0 || createSectionErrors.length > 0;

  // Check if there are any changes in edit view (for disabling submit button)
  // IMPORTANT: Only check changes for records that are currently visible (filtered)
  // If user hides old records, changes to those records should not enable submit button
  const hasEditViewChanges = hasUnsavedChanges;
  const hasEditViewErrors = rowsWithErrors.length > 0;
  const canSubmitFromEditView = hasEditViewChanges && !hasEditViewErrors;

  // Check if there are any changes in confirm view (update, create, or delete)
  // Note: tableUpdateData contains pairs (original + updated), so we check if it has any updated rows (odd indices)
  const hasUpdateChanges = tableUpdateData.filter((_, index) => index % 2 === 1).length > 0;
  const hasCreateChanges = tableCreateNewData.some((row) => !isUntouchedBlankCreateRow(row));
  const hasDeleteChanges = tableDeletedData.length > 0;
  const hasConfirmViewChanges = hasUpdateChanges || hasCreateChanges || hasDeleteChanges;

  const handleConfirmSubmit = async () => {
    // Validate before submitting
    if (hasConfirmErrors) {
      return; // Don't submit if there are errors
    }

    // Create a map of original planDeliveries by id to get displayOrder
    const originalPlansMap = createOriginalPlansMap(planDetail);

    // Convert all plans (update, create, delete)
    const updatePlans = convertUpdatePlans(
      tableUpdateData,
      plantOptions,
      witnessCompanyOptions,
      originalPlansMap
    );
    const createPlans = convertCreatePlans(tableCreateNewData, plantOptions, witnessCompanyOptions);
    const deletePlans = convertDeletePlans(
      tableDeletedData,
      plantOptions,
      witnessCompanyOptions,
      originalPlansMap
    );

    // Combine all plans
    const plans: UpdatePlanRequest[] = [...updatePlans, ...createPlans, ...deletePlans];

    // Prepare request
    const request: UpdateFullPlanRequest = {
      propertyId: planDetail?.property?.id as string,
      plans: plans,
    };
    try {
      setIsLoading(true);
      await updatePlanById(planDetail?.id as string, request);
      setSnackbarMessage(MESSAGES.EDIT_SUCCESS, { type: snackbarType.SUCCESS });

      // Set isSubmitting to true FIRST to bypass popup confirm when navigating after successful submit
      // This must be set before reset and navigate to ensure blocker sees it
      setIsSubmitting(true);

      // Reset form before navigation to prevent popup confirm
      resetAllForms();

      // Use requestAnimationFrame to ensure isSubmitting state is updated before navigate
      // This prevents popup confirm from showing after successful submit
      window.requestAnimationFrame(() => {
        // Success: redirect to plans list
        setIsLoading(false);
        navigate(paths.plans.path);
      });
    } catch (error) {
      console.error('[PlanDetailPage] Error updating plan', error);
      setIsLoading(false);
      setConfirmDialog({
        title: '登録に失敗しました。もう一度お試しください。',
      });
    }
  };

  // Mobile version - view only, no edit or confirm functionality
  if (isMobile) {
    return <PlanEditViewMobile planDetail={planDetail} />;
  }

  // Desktop version - supports both edit and confirm views
  // Confirm View
  if (viewMode === 'confirm') {
    return (
      <Box sx={{ width: '100%', py: 2, px: 2 }}>
        <PropertySection
          propertyName={getPropertySectionDisplayName(planDetail?.property)}
          propertyId={planDetail?.property?.id ?? ''}
        />
        <Box data-plan-header>
          <PlanHeader title='予定登録内容確認' subtitle='登録した予定の内容を確認してください。' />
        </Box>

        {/* Content Update Section */}
        <UpdateSection
          tableData={tableUpdateData}
          plantOptions={plantOptions}
          witnessCompanyOptions={witnessCompanyOptions}
          rowsWithErrors={updateSectionErrors}
          setRowsWithErrors={setUpdateSectionErrors}
          onDataChange={handleUpdateSectionDataChange}
        />

        {/* New Registration Section */}
        <CreateSection
          tableData={tableCreateNewData}
          plantOptions={plantOptions}
          witnessCompanyOptions={witnessCompanyOptions}
          rowsWithErrors={createSectionErrors}
          setRowsWithErrors={setCreateSectionErrors}
          onDataChange={handleCreateSectionDataChange}
        />

        {/* Deleted Section */}
        <DeleteSection
          tableData={tableDeletedData}
          plantOptions={plantOptions}
          witnessCompanyOptions={witnessCompanyOptions}
        />

        {/* Fixed Footer Actions */}
        <PlanActionsButton
          isSubmitting={false}
          isSubmitDisabled={hasConfirmErrors || !hasConfirmViewChanges}
          onCancel={handleCancel}
          onSubmit={handleConfirmSubmit}
          canUpdate={planDetail?.canUpdate || false}
          onAddRow={undefined}
          submitLabel='編集内容を決定'
        />
      </Box>
    );
  }

  // Desktop Edit View
  return (
    <PlanEditView
      planDetail={planDetail}
      filteredPlanDeliveries={filteredPlanDeliveries}
      tableData={tableData}
      fullTableData={fullTableData}
      visibleRowIndices={visibleRowIndices}
      editedRowIndices={editedRowIndices}
      oldRecordIndices={oldRecordIndices}
      plantOptions={plantOptions}
      witnessCompanyOptions={witnessCompanyOptions}
      rowsWithErrors={rowsWithErrors}
      setRowsWithErrors={setRowsWithErrors}
      countPlans={countPlans}
      filters={filters}
      setFilters={setFilters}
      handleAddRowAbove={handleAddRowAbove}
      handleAddRowBelow={handleAddRowBelow}
      handleDeleteRows={handleDeleteRows}
      handleTableDataChange={handleTableDataChange}
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      handleAddRow={handleAddRow}
      hasChanges={canSubmitFromEditView}
    />
  );
};
